/**
 * PATH: packages/ui/src/kanban/components/KanbanCardHoverMenu.tsx
 * FILE_ID: UI-KANBAN-CARD-HOVER-MENU-001
 * PURPOSE: The card's HOVER MENU — a slim vertical strip of icon buttons the
 *   card reveals on hover, sitting just OUTSIDE the card's right edge (in the
 *   lane gutter, beside the card, NOT over its content). Each opens a portaled
 *   panel further to the right. Items: Colour (labels + tile tint), Draft
 *   (compose an email from the card), Calculator + Flowchart (reserved, shipped
 *   disabled as "coming soon").
 * WHY:
 *   FEAT-2026-06-29-KANBAN-CARD-HOVER-MENU — operator: *"a right expansion that
 *   happens on hover of the card with menu items: Colour / Calculator / Draft /
 *   Flowchart"*. Supersedes the single-dot KanbanCardColorRail: the "Colour"
 *   item now also manages coloured LABELS (Tags, many-per-card) — operator:
 *   "colours should be also labels". Calculator (a per-card workbook) and
 *   Flowchart land in later phases; their icons render now (disabled) so the
 *   menu reads complete.
 *
 *   2026-07-01 — operator: *"This should be on the right of the card not inside
 *   the card"*. The strip used to be `absolute right-1` INSIDE the card, so it
 *   overlapped the card's top-right content. It now sits in the gutter beside
 *   the card. It CANNOT simply move to the card's right edge in-flow though:
 *
 *   HARD CONSTRAINT (why BOTH the strip and the panel are portaled): the lane
 *   card-list (`lane/LaneBody.tsx`) is `overflow-y-auto`, so per CSS the
 *   cross-axis `overflow-x:visible` is forced to `auto` too — anything past the
 *   card's right edge INSIDE the lane is clipped (only ~6px of lane padding is
 *   free). So the strip is portaled to <body> at `position:fixed`, anchored off
 *   a 0-width in-card marker's viewport rect (kept in-flow so it tracks the card
 *   as the lane scrolls / the card hover-expands), flipping to the card's left
 *   only if it would run off-screen. Because the strip is no longer a DOM
 *   descendant of the card, the card's `mouseleave` fires the instant the
 *   pointer crosses into the gutter. A short close-delay latch is not enough on
 *   tall expanded cards (strip is only ~150px and vertically centred) — an
 *   invisible full-card-height pointer-events bridge fills the gutter so the
 *   pointer never falls into dead space between card and Colour sheet.
 * FLOW:
 *   ┌──────────┐ hover ┌──────────────────┐ click ┌──────────────────────────┐
 *   │ collapsed │ ────▶ │ icon strip       │ ────▶ │ portal panel (colour|draft)│
 *   │ (card)    │       │ (portal, gutter) │       │ → attach/detach / compose  │
 *   └──────────┘       └──────────────────┘       └──────────────────────────┘
 * DEPENDENCIES:
 *   - react-dom createPortal: escape the lane's overflow clip (strip + panel).
 *   - lucide-react: Palette / PenSquare / Calculator / Workflow glyphs.
 *   - ../hooks/useDismissOnOutsideInteraction + ../zLayers.
 *   - ./KanbanCardLabelPicker (Colour panel) + ./KanbanCardComposePicker (Draft).
 * RELATED:
 *   - ./KanbanCard.tsx: renders this (replaces the old KanbanCardColorRail).
 */
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { createPortal } from 'react-dom'

import type { KanbanMember } from '../api'
import type { Card, Tag } from '../types'
import type { KanbanCardQuickActionHandlers } from './KanbanCard.types'
import { useDismissOnOutsideInteraction } from '../hooks/useDismissOnOutsideInteraction'
import { Z_LAYER } from '../zLayers'
import { KanbanCardComposePicker } from './KanbanCardComposePicker'
import { KanbanCardHoverMenuStrip } from './KanbanCardHoverMenuStrip'
import type { PanelKind } from './KanbanCardHoverMenu.types'
import { KanbanCardLabelPicker } from './KanbanCardLabelPicker'

export interface KanbanCardHoverMenuProps {
  card: Card
  /** True while the card is hovered/active — reveals the strip. Once a panel is
   *  open it stays up on its own (outside-click / Esc / leave). */
  visible: boolean
  /** Set / clear the tile tint token. Omit to hide the tint row. */
  onSetColor?: (card: Card, color: string) => Promise<unknown> | void
  /** The org labels offered in the Colour panel. */
  availableLabels?: Tag[]
  /** FEAT-2026-07-03-KANBAN-LABEL-EDIT-IN-PICKER — label registry editing
   *  (pencil edit + New label) inside the Colour panel. Omit → read-only. */
  labelRegistry?: KanbanCardQuickActionHandlers['labelRegistry']
  /** Attach / detach a coloured label (Tag) to/from the card. */
  onAttachLabel?: (card: Card, tagId: string) => Promise<unknown> | void
  onDetachLabel?: (card: Card, tagId: string) => Promise<unknown> | void
  /** Compose an email from the card (Draft). Omit to hide the Draft item. */
  onComposeEmail?: (card: Card, recipients: string[]) => void
  /** Org members offered in the Draft recipient picker. */
  members?: KanbanMember[]
  /** FEAT-2026-06-30-KANBAN-CARD-WORKBOOK-FLOWCHART — open the per-card
   *  Workbook / Flowchart editors. When wired, the Calculator / Flowchart
   *  icons become live buttons; omit either to keep that icon disabled
   *  ("coming soon"). The board lifts the modal state. */
  onOpenWorkbook?: (card: Card) => void
  /** Intent-only warm (modal + AG Grid + optional GET). Never call on board mount. */
  onPrefetchWorkbook?: (card: Card) => void
  onOpenFlowchart?: (card: Card) => void
}

const GAP = 8
const PANEL_W_ESTIMATE = 232
// Gutter offset: how far the icon strip floats off the card's right edge.
const STRIP_GAP = 6
const STRIP_W_ESTIMATE = 38
const STRIP_H_ESTIMATE = 150
// Match KanbanCardQuickActions grace so diagonal travel to Colour stays alive.
const CLOSE_DELAY_MS = 320
/** Bridge under strip/panel; strip above panel so Colour sheet cannot steal Calculator clicks. */
const BRIDGE_Z = Z_LAYER.popover - 1
const PANEL_Z = Z_LAYER.popover
const STRIP_Z = Z_LAYER.popover + 1
// FEAT-2026-07-02-KANBAN-CARD-HOVER-EXPAND: dwell before a hovered strip icon
// auto-opens its panel (operator: "hovering over the action items like Labels
// ... should automatically expand it"). Long enough that a pointer travelling
// down the strip doesn't pop each panel; short enough to feel instant.
const HOVER_OPEN_DWELL_MS = 160

interface PanelPos {
  left: number
  top: number
}

interface BridgeRect {
  left: number
  top: number
  width: number
  height: number
}

// The strip is ONE crisp floating capsule (border + soft shadow + blur), not a
// column of separate bubbles. Each icon is a borderless button that fills on
// hover and lights up (blue = the kanban accent) while its panel is open.
const STRIP_SHELL =
  'flex flex-col items-center gap-0.5 rounded-2xl border border-black/[0.07] bg-white/85 p-1 shadow-lg shadow-neutral-900/10 ring-1 ring-black/[0.02] backdrop-blur-md dark:border-white/10 dark:bg-zinc-900/85 dark:shadow-black/40 dark:ring-white/[0.04]'

export function KanbanCardHoverMenu({
  card,
  visible,
  onSetColor,
  availableLabels,
  labelRegistry,
  onAttachLabel,
  onDetachLabel,
  onComposeEmail,
  members,
  onOpenWorkbook,
  onPrefetchWorkbook,
  onOpenFlowchart,
}: KanbanCardHoverMenuProps) {
  const [panel, setPanel] = useState<PanelKind | null>(null)
  const [pos, setPos] = useState<PanelPos | null>(null)
  const [stripPos, setStripPos] = useState<PanelPos | null>(null)
  // Invisible hit target covering card→strip (and strip→panel) dead space so
  // keepOpen does not rely on the pointer landing on the short centred capsule.
  const [bridgePos, setBridgePos] = useState<BridgeRect | null>(null)
  // The strip stays up while the card is hovered, while the pointer is on the
  // strip/bridge itself, or while a panel is open. `keepOpen` latches that across the
  // card->gutter gap (the strip is portaled, so it is NOT a card descendant and
  // the card's mouseleave fires the instant the pointer leaves the card body).
  const [keepOpen, setKeepOpen] = useState(false)
  // Drives the entrance (fade + slide from the card). Flipped on via rAF once
  // the strip is mounted + positioned, so the transition is actually visible
  // (plugin-free; the repo has no tailwindcss-animate).
  const [stripReady, setStripReady] = useState(false)
  const panelRef = useRef<HTMLDivElement | null>(null)
  const stripRef = useRef<HTMLDivElement | null>(null)
  const anchorRef = useRef<HTMLSpanElement | null>(null)
  const anchorRect = useRef<DOMRect | null>(null)
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const readyRaf = useRef<number | null>(null)
  const hoverOpenTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  const hasColor =
    !!onSetColor
    || !!labelRegistry
    || (!!availableLabels?.length && !!onAttachLabel)
  const hasDraft = !!onComposeEmail
  const hasWorkbook = !!onOpenWorkbook
  const hasFlowchart = !!onOpenFlowchart

  const close = useCallback(() => setPanel(null), [])

  const cancelClose = useCallback(() => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current)
      closeTimer.current = null
    }
  }, [])
  const scheduleClose = useCallback(() => {
    cancelClose()
    closeTimer.current = setTimeout(() => {
      setKeepOpen(false)
      setPanel(null)
    }, CLOSE_DELAY_MS)
  }, [cancelClose])
  const holdOpen = useCallback(() => {
    cancelClose()
    setKeepOpen(true)
  }, [cancelClose])

  // Card hover drives the latch: revealed on enter, delayed-hide on leave so the
  // pointer can bridge the gutter to the (portaled) strip.
  useEffect(() => {
    if (visible) holdOpen()
    else scheduleClose()
  }, [visible, holdOpen, scheduleClose])

  const cancelHoverOpen = useCallback(() => {
    if (hoverOpenTimer.current) {
      clearTimeout(hoverOpenTimer.current)
      hoverOpenTimer.current = null
    }
  }, [])

  useEffect(
    () => () => {
      cancelClose()
      cancelHoverOpen()
      if (readyRaf.current != null && typeof cancelAnimationFrame !== 'undefined') {
        cancelAnimationFrame(readyRaf.current)
      }
    },
    [cancelClose, cancelHoverOpen],
  )

  const reposition = useCallback(() => {
    const r = anchorRect.current
    if (!r) return
    const w = panelRef.current?.offsetWidth || PANEL_W_ESTIMATE
    const h = panelRef.current?.offsetHeight || 40
    let left = r.right + GAP
    if (left + w > window.innerWidth - 8) left = r.left - GAP - w
    let top = r.top + r.height / 2 - h / 2
    top = Math.max(8, Math.min(top, window.innerHeight - h - 8))
    setPos({ left, top })
  }, [])

  // Position the icon strip just off the card's right edge, vertically centred
  // on the card. Also size the full-card-height hover bridge that covers the
  // empty gutter (and the open panel when present) so the pointer can travel
  // from any part of a tall card without dropping keepOpen.
  const repositionStrip = useCallback(() => {
    const a = anchorRef.current
    if (!a) return
    const r = a.getBoundingClientRect()
    const w = stripRef.current?.offsetWidth || STRIP_W_ESTIMATE
    const h = stripRef.current?.offsetHeight || STRIP_H_ESTIMATE
    let stripLeft = r.right + STRIP_GAP
    let flipped = false
    if (stripLeft + w > window.innerWidth - 4) {
      stripLeft = r.left - STRIP_GAP - w
      flipped = true
    }
    let stripTop = r.top + r.height / 2 - h / 2
    stripTop = Math.max(8, Math.min(stripTop, window.innerHeight - h - 8))
    setStripPos({ left: stripLeft, top: stripTop })

    // Bridge ONLY the empty gutter between card and strip (full card height).
    // Never cover the strip/panel bodies — overlapping them with a transparent
    // hit layer (even at a lower z-index) plus a tall Colour panel portaled
    // after the strip was intercepting Calculator / Flowchart clicks.
    let bridgeLeft: number
    let bridgeRight: number
    if (!flipped) {
      bridgeLeft = r.right
      bridgeRight = stripLeft
    } else {
      bridgeLeft = stripLeft + w
      bridgeRight = r.left
    }
    let bridgeTop = r.top
    let bridgeBottom = r.bottom

    const panelEl = panelRef.current
    const panelPos = pos
    if (panelEl && panelPos) {
      const pw = panelEl.offsetWidth || PANEL_W_ESTIMATE
      // Extend only through the strip→panel GAP, not under the panel chrome.
      if (!flipped) {
        const gapLeft = stripLeft + w
        const gapRight = panelPos.left
        if (gapRight > gapLeft) {
          bridgeLeft = Math.min(bridgeLeft, gapLeft)
          bridgeRight = Math.max(bridgeRight, gapRight)
        }
      } else {
        const gapRight = stripLeft
        const gapLeft = panelPos.left + pw
        if (gapRight > gapLeft) {
          bridgeLeft = Math.min(bridgeLeft, gapLeft)
          bridgeRight = Math.max(bridgeRight, gapRight)
        }
      }
      bridgeTop = Math.min(bridgeTop, panelPos.top)
      bridgeBottom = Math.max(bridgeBottom, panelPos.top + (panelEl.offsetHeight || 40))
    }

    const bridgeWidth = bridgeRight - bridgeLeft
    if (bridgeWidth >= 1) {
      setBridgePos({
        left: bridgeLeft,
        top: bridgeTop,
        width: bridgeWidth,
        height: Math.max(1, bridgeBottom - bridgeTop),
      })
    } else {
      setBridgePos(null)
    }
  }, [pos])

  useLayoutEffect(() => {
    if (!panel) {
      setPos(null)
      return
    }
    reposition()
    const onScroll = () => reposition()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
    }
  }, [panel, reposition])

  const shown = keepOpen || panel !== null

  useLayoutEffect(() => {
    if (readyRaf.current != null && typeof cancelAnimationFrame !== 'undefined') {
      cancelAnimationFrame(readyRaf.current)
      readyRaf.current = null
    }
    if (!shown) {
      setStripPos(null)
      setBridgePos(null)
      setStripReady(false)
      return
    }
    repositionStrip()
    // Mount positioned-but-hidden, then reveal next frame so the fade/slide
    // actually plays. rAF may be absent in jsdom -> fall back to instant-ready.
    if (typeof requestAnimationFrame !== 'undefined') {
      readyRaf.current = requestAnimationFrame(() => setStripReady(true))
    } else {
      setStripReady(true)
    }
    const onScroll = () => repositionStrip()
    window.addEventListener('scroll', onScroll, true)
    window.addEventListener('resize', onScroll)
    let ro: ResizeObserver | undefined
    if (typeof ResizeObserver !== 'undefined' && anchorRef.current) {
      ro = new ResizeObserver(() => repositionStrip())
      ro.observe(anchorRef.current)
    }
    return () => {
      window.removeEventListener('scroll', onScroll, true)
      window.removeEventListener('resize', onScroll)
      ro?.disconnect()
    }
  }, [shown, repositionStrip])

  // Panel open/move extends the bridge to cover strip→panel dead space.
  useLayoutEffect(() => {
    if (!shown || !panel) return
    repositionStrip()
  }, [shown, panel, pos, repositionStrip])

  useDismissOnOutsideInteraction(panelRef, close, panel !== null)

  // Open is a SET (never a toggle): re-clicking a strip button just re-anchors
  // the same panel rather than fighting the capture-phase outside-dismiss (which
  // would otherwise close-then-reopen). Closing is by outside-click / Esc / leave.
  function openPanel(kind: PanelKind, e: React.MouseEvent) {
    e.stopPropagation()
    e.preventDefault()
    cancelHoverOpen()
    anchorRect.current = e.currentTarget.getBoundingClientRect()
    holdOpen()
    setPanel(kind)
  }

  // FEAT-2026-07-02-KANBAN-CARD-HOVER-EXPAND: hovering a strip icon ARMS its
  // panel open after a short dwell, so Labels/Colour (and Draft) expand
  // without a click. Hover only OPENS (never closes an open panel); leaving
  // the icon before the dwell elapses cancels the arm. Workbook/Flowchart
  // stay click-only: they open full modals, not lightweight panels.
  function armPanelOnHover(kind: PanelKind, e: React.MouseEvent) {
    const rect = e.currentTarget.getBoundingClientRect()
    cancelHoverOpen()
    hoverOpenTimer.current = setTimeout(() => {
      anchorRect.current = rect
      holdOpen()
      setPanel((prev) => (prev === kind ? prev : kind))
    }, HOVER_OPEN_DWELL_MS)
  }

  if (!hasColor && !hasDraft && !hasWorkbook && !hasFlowchart) return null

  return (
    <>
      {/* In-flow 0-width anchor: pins the card's right-edge viewport rect so the
          portaled strip can float beside the card and track it. Carries
          data-card-hover-menu so hosts/tests can still find the menu on the card
          even though the visible strip is portaled to <body>. */}
      <span
        ref={anchorRef}
        data-card-hover-menu=""
        data-shown={shown ? 'true' : undefined}
        aria-hidden="true"
        className="pointer-events-none absolute inset-y-0 right-0 w-0"
      />

      {shown && typeof document !== 'undefined'
        && createPortal(
          <>
            {bridgePos && (
              <div
                data-card-hover-bridge=""
                data-testid="kanban-card-hover-bridge"
                aria-hidden="true"
                onMouseEnter={holdOpen}
                onMouseLeave={scheduleClose}
                style={{
                  position: 'fixed',
                  left: bridgePos.left,
                  top: bridgePos.top,
                  width: bridgePos.width,
                  height: bridgePos.height,
                  zIndex: BRIDGE_Z,
                  // Transparent hit target only — never paints over the lane.
                  background: 'transparent',
                }}
              />
            )}
            <div
              ref={stripRef}
              data-card-hover-strip=""
              onMouseEnter={holdOpen}
              onMouseLeave={scheduleClose}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                position: 'fixed',
                left: stripPos?.left ?? -9999,
                top: stripPos?.top ?? -9999,
                zIndex: STRIP_Z,
                transformOrigin: 'left center',
                opacity: stripReady ? 1 : 0,
                transform: stripReady ? 'none' : 'translateX(-6px) scale(0.94)',
                transition: 'opacity 150ms ease, transform 160ms cubic-bezier(0.22, 1, 0.36, 1)',
              }}
              className={STRIP_SHELL}
            >
            <KanbanCardHoverMenuStrip
              card={card}
              panel={panel}
              hasColor={hasColor}
              hasDraft={hasDraft}
              hasWorkbook={hasWorkbook}
              hasFlowchart={hasFlowchart}
              armPanelOnHover={armPanelOnHover}
              cancelHoverOpen={cancelHoverOpen}
              openPanel={openPanel}
              onOpenWorkbook={onOpenWorkbook}
              onPrefetchWorkbook={onPrefetchWorkbook}
              onOpenFlowchart={onOpenFlowchart}
              setPanel={setPanel}
            />
          </div>
          </>,
          document.body,
        )}

      {panel && typeof document !== 'undefined'
        && createPortal(
          <div
            ref={panelRef}
            role="group"
            aria-label={panel === 'color' ? 'Colour and labels' : 'Draft email'}
            data-testid={`kanban-card-hover-panel-${panel}`}
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'fixed',
              left: pos?.left ?? -9999,
              top: pos?.top ?? -9999,
              zIndex: PANEL_Z,
              visibility: pos ? 'visible' : 'hidden',
            }}
            className="overflow-hidden rounded-2xl border border-black/[0.07] bg-white/95 shadow-xl shadow-neutral-900/10 ring-1 ring-black/[0.02] backdrop-blur-md dark:border-white/10 dark:bg-zinc-900/95 dark:shadow-black/40 dark:ring-white/[0.04]"
          >
            {panel === 'color' && (
              <KanbanCardLabelPicker
                card={card}
                availableLabels={availableLabels ?? []}
                labelRegistry={labelRegistry}
                onAttachLabel={
                  onAttachLabel ? (tagId) => onAttachLabel(card, tagId) : undefined
                }
                onDetachLabel={
                  onDetachLabel ? (tagId) => onDetachLabel(card, tagId) : undefined
                }
                onSetColor={onSetColor ? (color) => onSetColor(card, color) : undefined}
              />
            )}
            {panel === 'draft' && onComposeEmail && (
              <div className="w-72">
                <KanbanCardComposePicker
                  members={members ?? []}
                  onSend={(recipients) => {
                    onComposeEmail(card, recipients)
                    setPanel(null)
                  }}
                />
              </div>
            )}
          </div>,
          document.body,
        )}
    </>
  )
}
